

/* ---------------------------------------------------------
   Geometry, straight off the shop drawing. Millimetres.
   Change a number here and both views update together.
   --------------------------------------------------------- */
const S = {
  L: 290, W: 58, R: 29,
  headSnaps: [24, 56],            // knurled ring sockets — the end the tail closes onto
  tailSnaps: [234, 266],          // plain domed studs
  postDia: 1.3,
  capDia: 11,                    // NOT on the drawing — visual estimate, confirm
  circle: {at: 145},              // inlay centre along the strap; the old 54.2 mm
                                  // layout circle is no longer drawn or specified
  inlayShift: 5.77,               // mm toward the base side. 5.77 equalises the leather
                                  // margin at both edges (11.68 mm each). Set to 0 to sit the
                                  // circumcentre dead centre, which is what IMG_1663 specifies.
  bezelWidth: 1.6,                // solder bead, mm
  tri: {side: 40}
};
/* Hardware finish follows the hide: gold on bone, silver on black. */
const LEATHER = {
  Bone:  {fill:'#EFE9DD', edge:'#C9BFAC', stitch:'#B9AE99',
          hw:{name:'gold',   grad:'brassGrad',  line:'#8E6C1B', hole:'#2A2114', gleam:'#F7EBC4'}},
  Black: {fill:'#23242A', edge:'#101116', stitch:'#3C3E46',
          hw:{name:'silver', grad:'nickelGrad', line:'#6E7883', hole:'#0E1013', gleam:'#FAFCFD'}}
};

/* Equilateral triangle, apex pointing ACROSS the strap (up when the strap
   runs horizontally) — matches the shop drawing and the finished pieces.
   Angles are in SVG space, where +y is down, so -90° is straight up. */
function triPath(cx, cy, side){
  const R = side / Math.sqrt(3);
  const pts = [-90, 30, 150].map(a => {
    const r = a * Math.PI / 180;
    return [cx + R * Math.cos(r), cy + R * Math.sin(r)];
  });
  return 'M' + pts.map(p => p.map(n => n.toFixed(2)).join(' ')).join(' L') + ' Z';
}

function renderView(L, glass){
  const cy = S.W / 2, cx = S.circle.at, ty = cy + S.inlayShift;
  const R = S.tri.side / Math.sqrt(3), cap = S.capDia / 2;
  /* head = knurled ring socket, tail = plain domed stud; finish follows the hide */
  const hw = L.hw;
  const socket = x => `
    <circle cx="${x}" cy="${cy}" r="${cap}" fill="url(#${hw.grad})"/>
    <circle cx="${x}" cy="${cy}" r="${cap * 0.72}" fill="none" stroke="${hw.line}" stroke-width=".5" opacity=".7"/>
    <circle cx="${x}" cy="${cy}" r="${cap * 0.34}" fill="${hw.hole}"/>`;
  const stud = x => `
    <circle cx="${x}" cy="${cy}" r="${cap * 0.82}" fill="url(#${hw.grad})"/>
    <circle cx="${x}" cy="${cy}" r="${cap * 0.82}" fill="none" stroke="${hw.line}" stroke-width=".35" opacity=".8"/>
    <ellipse cx="${x - cap * 0.18}" cy="${cy - cap * 0.22}" rx="${cap * 0.34}" ry="${cap * 0.26}" fill="${hw.gleam}" opacity=".5"/>`;

  return `<svg viewBox="-4 -4 ${S.L + 8} ${S.W + 8}" role="img"
      aria-label="${glass.name} crystal triangle set in a ${L.name.toLowerCase()} leather strap with ${hw.name} hardware, 290 by 58 millimetres">
    <rect x="0" y="0" width="${S.L}" height="${S.W}" rx="${S.R}" fill="${L.edge}"/>
    <rect x="0" y="0" width="${S.L}" height="${S.W - 1.4}" rx="${S.R}" fill="${L.fill}"/>
    <rect x="0" y="0" width="${S.L}" height="${S.W - 1.4}" rx="${S.R}" fill="${L.fill}" filter="url(#grain)"/>
    <rect x="4" y="4" width="${S.L - 8}" height="${S.W - 9.4}" rx="${S.R - 4}"
          fill="none" stroke="${L.stitch}" stroke-width=".7" stroke-dasharray="3 3" opacity=".7"/>
    ${S.headSnaps.map(socket).join('')}
    ${S.tailSnaps.map(stud).join('')}
    <path d="${triPath(cx, ty, S.tri.side)}" fill="none" stroke="url(#solderGrad)"
          stroke-width="${S.bezelWidth * 2}" stroke-linejoin="round"/>
    <path d="${triPath(cx, ty, S.tri.side)}" fill="${glass.hex}"/>
    <path d="M${cx} ${(ty - R).toFixed(2)} L${(cx + R * 0.866).toFixed(2)} ${(ty + R * 0.5).toFixed(2)} L${cx} ${ty} Z" fill="#ffffff" opacity=".24"/>
    <path d="M${cx} ${(ty - R).toFixed(2)} L${(cx - R * 0.866).toFixed(2)} ${(ty + R * 0.5).toFixed(2)} L${cx} ${ty} Z" fill="#000000" opacity=".18"/>
    <path d="M${(cx - R * 0.866).toFixed(2)} ${(ty + R * 0.5).toFixed(2)} L${(cx + R * 0.866).toFixed(2)} ${(ty + R * 0.5).toFixed(2)} L${cx} ${ty} Z" fill="#000000" opacity=".08"/>
  </svg>`;
}

function specView(){
  const cy = S.W / 2, cx = S.circle.at;
  const ink = '#8FA0B0', dim = '#5E7183', F = 'Jost, sans-serif';
  const tick = x => `
    <line x1="${x}" y1="${cy}" x2="${x}" y2="${S.W + 14}" stroke="${dim}" stroke-width=".35" stroke-dasharray="2 2"/>
    <circle cx="${x}" cy="${cy}" r="1.4" fill="${ink}"/>
    <text x="${x}" y="${S.W + 21}" font-size="6" fill="${dim}" text-anchor="middle" font-family="${F}">${x}</text>`;

  return `<svg viewBox="-16 -28 ${S.L + 36} ${S.W + 64}" role="img"
      aria-label="Dimensioned shop drawing: 290 by 58 millimetre strap with snap centres and a 40 millimetre triangle inlay">
    <line x1="0" y1="-15" x2="${S.L}" y2="-15" stroke="${dim}" stroke-width=".35"/>
    <line x1="0" y1="-18" x2="0" y2="-12" stroke="${dim}" stroke-width=".35"/>
    <line x1="${S.L}" y1="-18" x2="${S.L}" y2="-12" stroke="${dim}" stroke-width=".35"/>
    <text x="${S.L / 2}" y="-19" font-size="7" fill="${dim}" text-anchor="middle" font-family="${F}">290 mm</text>

    <line x1="-9" y1="0" x2="-9" y2="${S.W}" stroke="${dim}" stroke-width=".35"/>
    <text x="-12" y="${cy + 2.4}" font-size="7" fill="${dim}" text-anchor="end" font-family="${F}">58</text>

    <rect x="0" y="0" width="${S.L}" height="${S.W}" rx="${S.R}" fill="none" stroke="${ink}" stroke-width=".6"/>
    <line x1="-4" y1="${cy}" x2="${S.L + 4}" y2="${cy}" stroke="${dim}" stroke-width=".3" stroke-dasharray="6 2 1 2"/>
    <path d="${triPath(cx, cy + S.inlayShift, S.tri.side)}" fill="none" stroke="#C2992F" stroke-width=".9"/>
    <text x="${cx}" y="-5" font-size="6" fill="${dim}" text-anchor="middle" font-family="${F}">&#9651;40 mm at 145 mm &middot; 11.7 mm clear both edges</text>
    ${S.headSnaps.map(tick).join('')}
    ${S.tailSnaps.map(tick).join('')}
    <text x="${S.L / 2}" y="${S.W + 33}" font-size="6" fill="${dim}" text-anchor="middle" font-family="${F}">
      snap centres from head edge &middot; &#8709;1.3 mm post &middot; closes at 178 / 210 / 242 mm
    </text>
  </svg>`;
}

/* Four straps. Same body, same closure, same inlay — the core and
   backing differ. FILL THESE IN before launch; they drive the
   configurator, the comparison table, and the product copy. */



/* ------------------------------------------------------------
   Shopify controller. Reads Leather and Crystal off the real
   variant list so the drawing always matches the variant that
   is about to be added to the cart.
   ------------------------------------------------------------ */

/* ------------------------------------------------------------
   SVG defs. Injected from JS so the gradients travel with the
   renderer and cannot be lost when a layout or template changes.
   Without these, every snap and the solder bezel render black.
   ------------------------------------------------------------ */
(function(){
  function inject(){
    if (document.getElementById('aeon-defs')) return;
    var svg = document.createElementNS('http://www.w3.org/2000/svg','svg');
    svg.setAttribute('id','aeon-defs');
    svg.setAttribute('width','0'); svg.setAttribute('height','0');
    svg.setAttribute('aria-hidden','true');
    svg.style.position='absolute';
    svg.innerHTML =
      '<defs>' +
        '<linearGradient id="brassGrad" x1="0" y1="0" x2="1" y2="1">' +
          '<stop offset="0%" stop-color="#EBD48C"/><stop offset="50%" stop-color="#BC9128"/>' +
          '<stop offset="100%" stop-color="#8E6C1B"/></linearGradient>' +
        '<linearGradient id="nickelGrad" x1="0" y1="0" x2="1" y2="1">' +
          '<stop offset="0%" stop-color="#F3F6F8"/><stop offset="50%" stop-color="#B4BDC6"/>' +
          '<stop offset="100%" stop-color="#79838E"/></linearGradient>' +
        '<linearGradient id="solderGrad" x1="0" y1="0" x2="1" y2="1">' +
          '<stop offset="0%" stop-color="#F4F7F9"/><stop offset="38%" stop-color="#A8B2BD"/>' +
          '<stop offset="62%" stop-color="#E4EAEF"/><stop offset="100%" stop-color="#7E8A97"/></linearGradient>' +
        '<filter id="grain">' +
          '<feTurbulence type="fractalNoise" baseFrequency="1.4" numOctaves="3" result="n"/>' +
          '<feColorMatrix in="n" type="saturate" values="0"/>' +
          '<feComponentTransfer><feFuncA type="linear" slope=".12"/></feComponentTransfer>' +
          '<feComposite operator="in" in2="SourceGraphic"/></filter>' +
      '</defs>';
    document.body.insertBefore(svg, document.body.firstChild);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', inject);
  else inject();
})();

window.AEON = (function(){
  const CRYSTALS = {
    'Light Blue':'#4FC3E8', 'Purple':'#6D46B8', 'Yellow':'#D9A22B',
    'Ruby Red':'#B21D34', 'Pink':'#E58AA8', 'Aqua Blue':'#1B5AC4', 'Jade Green':'#91AF26'
  };

  function paint(root){
    const leather = root.dataset.leather || 'Bone';
    const crystal = root.dataset.crystal || 'Light Blue';
    const view    = root.dataset.view || 'render';
    const target  = root.querySelector('[data-strap-canvas]');
    if (!target) return;
    const L = Object.assign({name: leather}, LEATHER[leather] || LEATHER.Bone);
    target.innerHTML = view === 'spec'
      ? specView()
      : renderView(L, {name: crystal, hex: CRYSTALS[crystal] || '#4FC3E8'});
    const cap = root.querySelector('[data-cap-hardware]');
    if (cap) cap.textContent = L.hw.name.charAt(0).toUpperCase() + L.hw.name.slice(1);
    const cl = root.querySelector('[data-cap-leather]'); if (cl) cl.textContent = leather;
    const cc = root.querySelector('[data-cap-crystal]'); if (cc) cc.textContent = crystal;
  }

  function initToggles(root){
    root.querySelectorAll('[data-view-toggle]').forEach(b => {
      b.addEventListener('click', () => {
        root.querySelectorAll('[data-view-toggle]').forEach(x => x.setAttribute('aria-pressed','false'));
        b.setAttribute('aria-pressed','true');
        root.dataset.view = b.dataset.viewToggle;
        paint(root);
      });
    });
  }

  function initProduct(form){
    const root     = form.querySelector('[data-strap-preview]');
    const holder   = form.querySelector('[data-variants]');
    if (!holder) return;
    const variants = JSON.parse(holder.textContent);
    const idField  = form.querySelector('[name="id"]');
    const priceEl  = form.querySelector('[data-price]');
    const submit   = form.querySelector('[data-submit]');
    const money    = form.dataset.moneyFormat || '${{amount}}';

    function fmt(cents){
      return money.replace(/\{\{\s*amount\s*\}\}/, (cents/100).toFixed(2))
                  .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents/100)));
    }
    function chosen(){
      const o = {};
      form.querySelectorAll('[data-option]').forEach(g => {
        const on = g.querySelector('[aria-pressed="true"]');
        if (on) o[g.dataset.option] = on.dataset.value;
      });
      return o;
    }
    function sync(){
      const o = chosen();
      const want = Object.values(o);
      const v = variants.find(x => want.every(val => x.options.indexOf(val) !== -1));
      if (root){
        if (o.Leather) root.dataset.leather = o.Leather;
        if (o.Crystal) root.dataset.crystal = o.Crystal;
        paint(root);
      }
      if (v){
        if (idField) idField.value = v.id;
        if (priceEl) priceEl.textContent = fmt(v.price);
        if (submit){
          submit.disabled = !v.available;
          submit.textContent = v.available ? submit.dataset.label : 'Unavailable';
        }
      }
    }
    form.querySelectorAll('[data-option] [data-value]').forEach(b => {
      b.addEventListener('click', () => {
        b.closest('[data-option]').querySelectorAll('[data-value]')
          .forEach(x => x.setAttribute('aria-pressed','false'));
        b.setAttribute('aria-pressed','true');
        sync();
      });
    });
    if (root) initToggles(root);
    sync();
  }

  function start(){
    document.querySelectorAll('[data-strap-preview]').forEach(r => {
      if (!r.closest('[data-product-form]')) { paint(r); initToggles(r); }
    });
    document.querySelectorAll('[data-product-form]').forEach(initProduct);
    document.querySelectorAll('[data-strap-thumb]').forEach(el => {
      const L = Object.assign({name:'Bone'}, LEATHER.Bone);
      el.innerHTML = renderView(L, {name:'Light Blue', hex: CRYSTALS[el.dataset.strapThumb] || '#4FC3E8'});
    });
    const yr = document.getElementById('yr');
    if (yr) yr.textContent = new Date().getFullYear();
  }
  document.addEventListener('DOMContentLoaded', start);
  document.addEventListener('shopify:section:load', start);
  return {start: start, paint: paint};
})();

/* ------------------------------------------------------------
   Homepage configurator: strap + leather + crystal across the
   whole collection, adding the matching variant to the cart.
   ------------------------------------------------------------ */
(function(){
  const CRYSTAL_HEX = {
    'Light Blue':'#4FC3E8','Purple':'#6D46B8','Yellow':'#D9A22B','Ruby Red':'#B21D34',
    'Pink':'#E58AA8','Aqua Blue':'#1B5AC4','Jade Green':'#91AF26'
  };
  const LEATHER_HEX = {'Bone':'#EFE9DD','Black':'#23242A'};

  function initCfg(root){
    const holder = root.querySelector('[data-catalogue]');
    if (!holder) return;
    let cat = [];
    try { cat = JSON.parse(holder.textContent); } catch(e){ return; }
    if (!cat.length) return;

    const money = root.dataset.moneyFormat || '${{amount}}';
    const fmt = c => money.replace(/\{\{\s*amount\s*\}\}/, (c/100).toFixed(2))
                          .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(c/100)));

    const preview = root.querySelector('[data-strap-preview]');
    const state = {i:0, Leather:'Bone', Crystal:'Light Blue'};

    /* option values, in the order Shopify returns them */
    function valuesFor(name){
      const seen = [];
      cat.forEach(p => p.variants.forEach(v => v.options.forEach(o => {
        const isLeather = Object.prototype.hasOwnProperty.call(LEATHER_HEX, o);
        const isCrystal = Object.prototype.hasOwnProperty.call(CRYSTAL_HEX, o);
        if (((name === 'Leather' && isLeather) || (name === 'Crystal' && isCrystal)) && seen.indexOf(o) === -1) seen.push(o);
      })));
      return seen;
    }

    function buildButtons(){
      const pw = root.querySelector('[data-cfg-product]');
      pw.innerHTML = cat.map((p,i) =>
        `<button type="button" class="opt" data-i="${i}" aria-pressed="${i===0}">${p.title}</button>`).join('');

      root.querySelectorAll('[data-cfg-option]').forEach(g => {
        const name = g.dataset.cfgOption;
        const vals = valuesFor(name);
        g.innerHTML = vals.map((v,i) => {
          const hex = name === 'Leather' ? (LEATHER_HEX[v] || '#EFE9DD') : (CRYSTAL_HEX[v] || '#4FC3E8');
          const cls = name === 'Leather' ? 'sw sw--leather' : 'sw';
          return `<button type="button" class="${cls}" style="--c:${hex}" data-v="${v}" aria-pressed="${i===0}" aria-label="${v}"><i></i></button>`;
        }).join('');
        if (vals.length) state[name] = vals[0];
      });
    }

    function current(){
      const p = cat[state.i];
      if (!p) return null;
      return p.variants.find(v =>
        v.options.indexOf(state.Leather) !== -1 && v.options.indexOf(state.Crystal) !== -1) || null;
    }

    function sync(){
      const v = current();
      if (preview){
        preview.dataset.leather = state.Leather;
        preview.dataset.crystal = state.Crystal;
        window.AEON.paint(preview);
      }
      const cp = root.querySelector('[data-cap-product]');
      if (cp) cp.textContent = cat[state.i] ? cat[state.i].title : '';

      const price = root.querySelector('[data-cfg-price]');
      const add   = root.querySelector('[data-cfg-add]');
      if (v){
        if (price) price.textContent = fmt(v.price);
        const o1 = root.querySelector('[data-offer-1]'),
              o2 = root.querySelector('[data-offer-2]'),
              o3 = root.querySelector('[data-offer-3]');
        if (o1) o1.textContent = fmt(v.price);
        if (o2) o2.textContent = fmt(v.price * 2);
        if (o3) o3.textContent = fmt(v.price * 2);
        if (add){
          add.disabled = !v.available;
          add.textContent = v.available ? add.dataset.label : 'Unavailable';
        }
      } else if (add){
        add.disabled = true;
        add.textContent = 'Unavailable';
      }
    }

    root.addEventListener('click', e => {
      const p = e.target.closest('[data-cfg-product] [data-i]');
      if (p){
        root.querySelectorAll('[data-cfg-product] [data-i]').forEach(x => x.setAttribute('aria-pressed','false'));
        p.setAttribute('aria-pressed','true');
        state.i = parseInt(p.dataset.i, 10);
        return sync();
      }
      const s = e.target.closest('[data-cfg-option] [data-v]');
      if (s){
        const g = s.closest('[data-cfg-option]');
        g.querySelectorAll('[data-v]').forEach(x => x.setAttribute('aria-pressed','false'));
        s.setAttribute('aria-pressed','true');
        state[g.dataset.cfgOption] = s.dataset.v;
        return sync();
      }
    });

    const add = root.querySelector('[data-cfg-add]');
    if (add) add.addEventListener('click', () => {
      const v = current();
      if (!v) return;
      add.disabled = true; add.textContent = 'Adding…';
      fetch('/cart/add.js', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({items:[{id:v.id, quantity:1}]})
      }).then(r => r.json()).then(() => {
        add.textContent = 'Added — view cart';
        add.disabled = false;
        setTimeout(() => { add.textContent = add.dataset.label; }, 2400);
      }).catch(() => {
        add.textContent = 'Something went wrong';
        add.disabled = false;
        setTimeout(() => { add.textContent = add.dataset.label; }, 2400);
      });
    });

    buildButtons();
    sync();
  }

  function boot(){ document.querySelectorAll('[data-configurator]').forEach(initCfg); }
  document.addEventListener('DOMContentLoaded', boot);
  document.addEventListener('shopify:section:load', boot);
})();

/* ------------------------------------------------------------
   Anchor scrolling: measure the sticky header so in-page links
   land below it, and make cross-page /#anchor links work.
   ------------------------------------------------------------ */
(function(){
  function measure(){
    const bar  = document.querySelector('.promo');
    const mast = document.querySelector('.mast');
    if (!mast) return;
    /* the promo bar scrolls away; only the sticky masthead needs clearing */
    const h = Math.round(mast.getBoundingClientRect().height) + 16;
    document.documentElement.style.setProperty('--header-h', h + 'px');
  }
  function jump(){
    if (!location.hash) return;
    const el = document.getElementById(location.hash.slice(1));
    if (el) setTimeout(() => el.scrollIntoView({behavior:'auto', block:'start'}), 60);
  }
  window.addEventListener('resize', measure);
  document.addEventListener('DOMContentLoaded', function(){ measure(); jump(); });
  document.addEventListener('shopify:section:load', measure);
})();
